// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// The admin file list's type strings map onto the shared fileSelection kinds:
// 'zip' rows are panorama zips, 'poletagimage' rows are pole tag image zips.
// The kind decides the download route (pano/poleImage by id, others by name).
export const FILE_TYPE_TO_KIND: Record<string, FileSelectionKind> = {
  tif: 'tif', las: 'las', zip: 'pano', poletagimage: 'poleImage',
};

// Same section names as the customer Data Sets panel (D75) and the map's
// footprint kind list (D78).
const SECTION_LABELS: Record<string, string> = {
  tif: 'Imagery', las: 'Point Clouds', zip: 'Panoramas', poletagimage: 'Pole Images',
};

interface SectionableFile {
  id: string;
  name: string;
  type: string;
}

export interface FileSection<T extends SectionableFile> {
  type: string;
  // null marks the untitled remainder for unknown types: rows render, but
  // there is no header, no bulk download (the by-name route is not a safe
  // guess for a type this list has never seen) and no selection gestures.
  label: string | null;
  files: T[];
  downloadRefs: IDownloadableFileRef[];
  // Displayed order for the section's kind, feeding shift-range selection.
  orderedIds: string[];
}

// Slices an already type-ordered file list into labelled kind sections, each
// carrying the IDownloadableFileRefs its BulkDownloadRow needs. Types outside
// typeOrder land in one trailing untitled section rather than being dropped.
export const buildFileSections = <T extends SectionableFile>(
  files: T[],
  typeOrder: string[],
): FileSection<T>[] => {
  const sections: FileSection<T>[] = [];
  typeOrder.forEach((type) => {
    const typeFiles = files.filter((file) => file.type === type);
    if (!typeFiles.length) return;
    sections.push({
      type,
      label: SECTION_LABELS[type] || type,
      files: typeFiles,
      downloadRefs: typeFiles.map((file) => ({
        kind: FILE_TYPE_TO_KIND[type], id: file.id, name: file.name,
      })),
      orderedIds: typeFiles.map((file) => file.id),
    });
  });
  const unknownFiles = files.filter((file) => !typeOrder.includes(file.type));
  if (unknownFiles.length) {
    sections.push({ type: 'other', label: null, files: unknownFiles, downloadRefs: [], orderedIds: [] });
  }
  return sections;
};

export default buildFileSections;
